import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { visibleToSupplier } from '../cgOrders/jobStages';
import type { SupabaseClient } from '../../lib/supabase';
// The submissions root key. Spelled 'supplier-exchange' here until now, while
// the real key is 'supplierExchange' — so this invalidation was a no-op and
// confirming an order never refreshed the slots it had just created.
import { exchangeKeys } from './hooks';

/**
 * The supplier's view of a CG order.
 *
 * Everything here goes through SECURITY DEFINER functions. There is no grant
 * on cg_orders or cg_order_items for a supplier and there must never be one —
 * cg_order_items.unit_price_usd is what the client pays, and a policy that let
 * a factory read the row "but not that column" is not a thing Row Level
 * Security can express.
 *
 * The organization is passed in, never inferred. is_active_supplier_of()
 * returns true for a super admin against every organization, so a query that
 * worked the org out for itself would show a mimicking super admin whichever
 * factory came back first.
 */

export interface SupplierOrderLine {
  units: number;
  name: string;
  sku: string;
  spec: string;
}

export interface SupplierOrder {
  order_id: string;
  order_no: string;
  status: string;
  ship_to: string;
  target_ship_date: string | null;
  supplier_confirmed_at: string | null;
  supplier_revision_note: string;
  sample_due_days: number;
  sample_clock_started_at: string | null;
  /** The submission holding this order's four component slots, once confirmed. */
  submission_id: string | null;
  items: SupplierOrderLine[];
  /**
   * Logistics and proofing, added to the letterbox in 20261006000100.
   *
   * These have been returned by supplier_orders() since that migration and
   * silently dropped here — the spread carried them at runtime but nothing was
   * typed, so nothing could read them. That is why the portal had no way to
   * show a tracking number it already had, or which component a client sent
   * back. Still no money: the RPC returns no price, cost or margin column and
   * this type must never grow one.
   */
  tracking_number: string | null;
  courier_name: string;
  supplier_ready_for_shipment: boolean;
  supplier_ready_at: string | null;
  assets: SupplierOrderAsset[];
  /**
   * When this job last changed stage. Drives the day counter on the board.
   *
   * One number per card, from a column already on the row — the full stage
   * history is a query per job and belongs on the job's own page, not on a
   * board whose purpose is showing every job at once.
   */
  status_changed_at: string;
  date_delivered: string | null;
  /** Where to follow the shipment. '' when nobody has pasted one. */
  tracking_url: string;
  /**
   * The SECOND promise: days to have the order ready for collection once the
   * samples are approved. A factory can be quick with samples and slow to
   * build, and one number averaged both into a figure describing neither.
   */
  production_due_days: number;
  /** Starts when the samples are approved. Null before that. */
  production_clock_started_at: string | null;
  /**
   * Out of sight, not merely finished — carried from cg_statuses rather than
   * worked out here (20261120000100).
   *
   * The board used to keep its own list, `new Set(['Delivered', 'Closed'])`,
   * and hid both. TAP's pipeline hides Closed alone, so a job Kyu had just
   * marked Delivered was on one board and missing from the other. The rule
   * lives in one place now and travels on the row.
   */
  is_archived: boolean;
}

/** One proof slot, as the factory is allowed to see it — no client note. */
export interface SupplierOrderAsset {
  component: string;
  version: number;
  state: string;
  url: string;
}

export const supplierOrderKeys = {
  all: ['supplier-orders'] as const,
  forOrg: (organizationId: string) => [...supplierOrderKeys.all, organizationId] as const,
};

export async function fetchSupplierOrders(
  client: SupabaseClient,
  organizationId: string,
): Promise<SupplierOrder[]> {
  const { data, error } = await client.rpc('supplier_orders', { p_org: organizationId });
  if (error) {
    throw new Error(error.message);
  }
  // items comes back as Json — the generated type cannot know the shape of a
  // jsonb_agg. Narrowed here rather than trusted: a row whose items are not an
  // array renders an empty line list instead of throwing on .map().
  //
  // The filter is the browser half of 20261118000100. The database is the
  // rule now — supplier_orders no longer returns a pre-PO order — and this
  // says the same thing where somebody reading the portal can see it.
  // visibleToSupplier() was written, documented and tested for precisely this
  // and had no caller anywhere, which is how the board came to offer a card
  // the job page then refused to open with "it may have been reassigned to
  // another supplier".
  return (data ?? [])
    .filter((row) => visibleToSupplier({ status: row.status }))
    .map((row) => ({
      ...row,
      items: Array.isArray(row.items) ? (row.items as unknown as SupplierOrderLine[]) : [],
      // Same treatment as items: a jsonb_agg the generated type cannot
      // describe. An unexpected shape renders as "no components" rather than
      // throwing on a page the factory needs in order to do anything at all.
      assets: Array.isArray(row.assets) ? (row.assets as unknown as SupplierOrderAsset[]) : [],
    }));
}

export function useSupplierOrders(organizationId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: supplierOrderKeys.forOrg(organizationId),
    queryFn: () => fetchSupplierOrders(client, organizationId),
    // The org is in the key, not just the argument. Without it a mimicking
    // super admin switching factories would be served the previous one's
    // cached orders — the exact bug the portal shipped with once.
    enabled: organizationId !== '',
    retry: false,
  });
}

export function useConfirmSupplierOrder(organizationId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await client.rpc('confirm_supplier_order', { p_order: orderId });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrderKeys.forOrg(organizationId) });
      // Confirming creates the submission that holds the four slots, so the
      // submission list is stale the moment this succeeds.
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.all });
    },
  });
}

export function useRequestOrderRevision(organizationId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; note: string }) => {
      const { error } = await client.rpc('request_order_revision', {
        p_order: input.orderId,
        p_note: input.note,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrderKeys.forOrg(organizationId) });
    },
  });
}

/** Refusals from the two RPCs, in words a factory can act on. */
export function supplierOrderErrorMessage(raw: string): string {
  if (raw.includes('order_already_confirmed')) {
    return 'This order was already confirmed. Refresh to see its current state.';
  }
  if (raw.includes('order_not_awaiting_confirmation')) {
    return 'This order is no longer waiting on confirmation. Check with TAP before starting.';
  }
  if (raw.includes('not_your_order')) {
    return 'That order belongs to another organisation.';
  }
  if (raw.includes('order_has_no_supplier')) {
    return 'This order is not linked to a supplier yet. TAP has to issue the PO first.';
  }
  if (raw.includes('supplier_cannot_change_order_status')) {
    return 'Only TAP can move an order between stages. Mark the order ready instead and TAP will take it from there.';
  }
  if (raw.includes('component_not_awaiting_revision')) {
    return 'That component is not waiting on a new version.';
  }
  if (raw.includes('revision_note_required')) {
    return 'Say what needs changing before sending the request.';
  }
  return 'That could not be saved. Try again.';
}

/**
 * Mark an order ready for collection, with the tracking number.
 *
 * supplier_set_shipment has existed since 20261006000100 and has never been
 * called from anywhere — no hook, no button. So the one thing a factory most
 * needs to tell TAP, at the end of a production run, had no way to be said in
 * the portal, and the tracking_number and courier_name columns sat empty while
 * the information travelled by WhatsApp.
 *
 * Marking ready is deliberately NOT a status change. The database refuses
 * supplier-made status writes; this raises a flag that somebody at TAP reads
 * before promoting the order, which is what keeps the financial gate and the
 * shipping decision with the people accountable for them.
 */
export function useSetShipment(organizationId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orderId: string;
      tracking: string;
      courier: string;
      ready: boolean;
      note: string;
      /** Only needed for a courier the browser cannot build a link for. */
      url?: string;
    }) => {
      const { error } = await client.rpc('supplier_set_shipment', {
        p_order: input.orderId,
        p_tracking: input.tracking,
        p_courier: input.courier,
        p_ready: input.ready,
        p_note: input.note,
        p_url: input.url ?? '',
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrderKeys.forOrg(organizationId) });
    },
  });
}
